import React, { useState, useEffect, useCallback } from 'react';
import { auditAPI } from '../../api/api';
import { Plus, CheckCircle2, XCircle, AlertCircle, MinusCircle, ChevronDown, ChevronUp, Save } from 'lucide-react';

const CHECKLIST_STATUS_ICONS = {
  'Complied': { icon: CheckCircle2, color: 'text-emerald-600' },
  'Not Complied': { icon: XCircle, color: 'text-red-600' },
  'Partially Complied': { icon: AlertCircle, color: 'text-amber-600' },
  'Not Applicable': { icon: MinusCircle, color: 'text-gray-400' },
};

const CHECKLIST_STATUS_BG = {
  'Complied': 'bg-emerald-50 border-emerald-200',
  'Not Complied': 'bg-red-50 border-red-200',
  'Partially Complied': 'bg-amber-50 border-amber-200',
  'Not Applicable': 'bg-gray-50 border-gray-200',
};

const DEFAULT_TEMPLATES = {
  'Finance & Accounts': [
    'Verify vouching of expenses with supporting documents',
    'Check if all journal entries are properly authorized',
    'Review bank reconciliation statements',
    'Verify petty cash records and approvals',
    'Check fixed asset register and depreciation',
  ],
  'GST Compliance': [
    'Verify GSTR-1 filing timeliness and accuracy',
    'Verify GSTR-3B filing and payment',
    'Check input tax credit reconciliation with GSTR-2B',
    'Review GST on reverse charge mechanism',
    'Verify e-way bill compliance',
  ],
  'TDS Compliance': [
    'Verify TDS deduction on applicable payments',
    'Check timely deposit of TDS',
    'Verify TDS return filing (24Q, 26Q, 27Q)',
    'Review TDS certificates issued (Form 16/16A)',
    'Check lower deduction certificates if applicable',
  ],
  'Payroll Controls': [
    'Verify employee attendance and leave records',
    'Check salary computation and statutory deductions',
    'Verify PF and ESI compliance',
    'Review bonus and gratuity provisions',
    'Check professional tax compliance',
  ],
  'Bank Controls': [
    'Verify bank mandate and authorized signatories',
    'Review cheque issuance and receipt controls',
    'Check online banking access controls',
    'Verify bank guarantee and LC records',
    'Review bank charge reconciliation',
  ],
  'Purchase Controls': [
    'Verify purchase order authorization process',
    'Check goods receipt note matching',
    'Review vendor evaluation and selection',
    'Verify purchase invoice verification process',
    'Check vendor payment authorization',
  ],
};

const AuditChecklists = ({ engagement }) => {
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedArea, setSelectedArea] = useState('Finance & Accounts');
  const [customItems, setCustomItems] = useState('');
  const [expandedChecklist, setExpandedChecklist] = useState(null);

  const auditAreas = Object.keys(DEFAULT_TEMPLATES);

  const loadChecklists = useCallback(async () => {
    if (!engagement?.id) return;
    setLoading(true);
    try {
      const res = await auditAPI.getChecklists(engagement.id);
      setChecklists(res.data);
      if (res.data.length > 0) setExpandedChecklist(prev => prev || res.data[0].id);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [engagement?.id]);

  useEffect(() => { loadChecklists(); }, [loadChecklists]);

  const handleCreateChecklist = async (useTemplate) => {
    const items = useTemplate
      ? (DEFAULT_TEMPLATES[selectedArea] || []).map(d => ({ description: d, status: 'Not Applicable', remarks: '', evidence: '' }))
      : customItems.split('\n').filter(l => l.trim()).map(d => ({ description: d.trim(), status: 'Not Applicable', remarks: '', evidence: '' }));

    if (items.length === 0) return alert('Add at least one checklist item');

    try {
      await auditAPI.createChecklist({ audit_id: engagement.id, area: selectedArea, items });
      setShowAddForm(false);
      setCustomItems('');
      loadChecklists();
    } catch (e) { console.error(e); alert('Failed to create checklist'); }
  };

  const handleUpdateItem = async (checklistId, itemId, status, remarks, evidence) => {
    try {
      await auditAPI.updateChecklistItem(checklistId, itemId, { status, remarks, evidence });
      loadChecklists();
    } catch (e) { console.error(e); }
  };

  const getComplianceStats = (items) => {
    const total = items.length;
    const complied = items.filter(i => i.status === 'Complied').length;
    const notComplied = items.filter(i => i.status === 'Not Complied').length;
    const partial = items.filter(i => i.status === 'Partially Complied').length;
    const na = items.filter(i => i.status === 'Not Applicable').length;
    return { total, complied, notComplied, partial, na };
  };

  return (
    <div className="space-y-4" data-testid="audit-checklists-tab">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-gray-800">Audit Checklists</h3>
        <button
          onClick={() => setShowAddForm(true)}
          className="flex items-center gap-1.5 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700"
          data-testid="add-checklist-btn"
        >
          <Plus className="w-4 h-4" /> Add Checklist
        </button>
      </div>

      {/* Add Checklist Form */}
      {showAddForm && (
        <div className="bg-white rounded-xl border p-5 space-y-4" data-testid="add-checklist-form">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-gray-800">New Checklist</h4>
            <button onClick={() => setShowAddForm(false)} className="text-sm text-gray-500 hover:text-gray-700">Cancel</button>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Audit Area</label>
            <select value={selectedArea} onChange={e => setSelectedArea(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="checklist-area-select">
              {auditAreas.map(a => <option key={a} value={a}>{a}</option>)}
              <option value="HR / Admin Controls">HR / Admin Controls</option>
              <option value="Inventory">Inventory</option>
              <option value="Statutory Compliance">Statutory Compliance</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => handleCreateChecklist(true)}
              disabled={!DEFAULT_TEMPLATES[selectedArea]}
              className="flex-1 py-2 bg-indigo-50 text-indigo-700 rounded-lg text-sm font-medium hover:bg-indigo-100 disabled:opacity-40"
              data-testid="use-template-btn"
            >
              Use Template ({(DEFAULT_TEMPLATES[selectedArea] || []).length} items)
            </button>
            <span className="self-center text-xs text-gray-400">or</span>
            <button
              onClick={() => handleCreateChecklist(false)}
              disabled={!customItems.trim()}
              className="flex-1 py-2 bg-gray-50 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-100 disabled:opacity-40"
              data-testid="use-custom-btn"
            >
              Use Custom Items
            </button>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Custom Items (one per line)</label>
            <textarea
              value={customItems}
              onChange={e => setCustomItems(e.target.value)}
              rows={4}
              placeholder="Enter each checklist item on a new line..."
              className="w-full border rounded-lg px-3 py-2 text-sm"
              data-testid="custom-items-input"
            />
          </div>
        </div>
      )}

      {/* Checklists List */}
      {checklists.length === 0 && !showAddForm && (
        <div className="text-center py-12 text-gray-400">
          <CheckCircle2 className="w-10 h-10 mx-auto mb-3 opacity-50" />
          <p className="font-medium">No checklists yet</p>
          <p className="text-sm">Add a checklist with predefined templates or custom items</p>
        </div>
      )}

      {checklists.map(cl => {
        const stats = getComplianceStats(cl.items || []);
        const isExpanded = expandedChecklist === cl.id;
        return (
          <div key={cl.id} className="bg-white rounded-xl border" data-testid={`checklist-${cl.id}`}>
            {/* Checklist Header */}
            <div
              className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
              onClick={() => setExpandedChecklist(isExpanded ? null : cl.id)}
            >
              <div>
                <h4 className="font-medium text-gray-800">{cl.area}</h4>
                <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                  <span className="text-emerald-600 font-medium">{stats.complied} Complied</span>
                  <span className="text-red-600 font-medium">{stats.notComplied} Not Complied</span>
                  <span className="text-amber-600 font-medium">{stats.partial} Partial</span>
                  <span className="text-gray-400">{stats.na} N/A</span>
                  <span>| {stats.total} items</span>
                </div>
              </div>
              {/* Progress bar */}
              <div className="flex items-center gap-3">
                <div className="w-24 bg-gray-100 rounded-full h-2 hidden sm:block">
                  <div className="h-2 rounded-full bg-emerald-500" style={{ width: `${stats.total ? (stats.complied / stats.total) * 100 : 0}%` }} />
                </div>
                {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
              </div>
            </div>

            {/* Checklist Items */}
            {isExpanded && (
              <div className="border-t divide-y">
                {(cl.items || []).map((item, idx) => (
                  <ChecklistItem
                    key={item.id}
                    item={item}
                    index={idx}
                    checklistId={cl.id}
                    onUpdate={handleUpdateItem}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const ChecklistItem = ({ item, index, checklistId, onUpdate }) => {
  const [remarks, setRemarks] = useState(item.remarks || '');
  const [evidence, setEvidence] = useState(item.evidence || '');
  const [showDetails, setShowDetails] = useState(false);
  const StatusInfo = CHECKLIST_STATUS_ICONS[item.status] || CHECKLIST_STATUS_ICONS['Not Applicable'];

  const handleStatusChange = (newStatus) => {
    onUpdate(checklistId, item.id, newStatus, remarks, evidence);
  };

  const handleSaveDetails = () => {
    onUpdate(checklistId, item.id, item.status, remarks, evidence);
    setShowDetails(false);
  };

  return (
    <div className={`p-3 ${CHECKLIST_STATUS_BG[item.status] || 'bg-white'} border-l-4 ${item.status === 'Complied' ? 'border-l-emerald-500' : item.status === 'Not Complied' ? 'border-l-red-500' : item.status === 'Partially Complied' ? 'border-l-amber-500' : 'border-l-gray-300'}`} data-testid={`checklist-item-${item.id}`}>
      <div className="flex items-start gap-3">
        <StatusInfo.icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${StatusInfo.color}`} />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-800">{item.description}</p>
          {item.checked_by && (
            <p className="text-xs text-gray-400 mt-0.5">Checked by: {item.checked_by}</p>
          )}
          {item.remarks && !showDetails && (
            <p className="text-xs text-gray-500 mt-1 italic">Remarks: {item.remarks}</p>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <select
            value={item.status}
            onChange={e => handleStatusChange(e.target.value)}
            className="text-xs border rounded px-1.5 py-1 bg-white"
            data-testid={`item-status-${item.id}`}
          >
            <option value="Not Applicable">N/A</option>
            <option value="Complied">Complied</option>
            <option value="Not Complied">Not Complied</option>
            <option value="Partially Complied">Partial</option>
          </select>
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="p-1 text-gray-400 hover:text-gray-600"
            title="Add remarks/evidence"
          >
            <Save className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      {showDetails && (
        <div className="mt-2 ml-8 space-y-2">
          <input type="text" value={remarks} onChange={e => setRemarks(e.target.value)} placeholder="Remarks..." className="w-full border rounded px-2 py-1.5 text-xs" data-testid={`item-remarks-${item.id}`} />
          <input type="text" value={evidence} onChange={e => setEvidence(e.target.value)} placeholder="Evidence reference..." className="w-full border rounded px-2 py-1.5 text-xs" data-testid={`item-evidence-${item.id}`} />
          <button onClick={handleSaveDetails} className="px-3 py-1 bg-indigo-600 text-white rounded text-xs hover:bg-indigo-700" data-testid={`save-item-${item.id}`}>Save</button>
        </div>
      )}
    </div>
  );
};

export default AuditChecklists;
